import StaffScanWaiting, { ScanLabel } from '../components/StaffScanWaiting';
/**
 * 쿠폰 수동 차감 (tools) — Step 2 FE
 * ============================================
 * 기반: docs/zlzl_coupon_tools_redeem_dev_steps.md v2.7 §4 / without_kl §7.2
 *
 * UI 패턴 (v2.8 재작성, frontend-design):
 *   - 은행 공통 헤더와 밝은 녹색 톤, 중앙 바코드 대기화면
 *   - 스캔 이후 사용/취소 결과 레이아웃과 처리 흐름 유지
 *   - 키오스크 태블릿 가로 모드 (1280×800)
 *
 * 흐름:
 *   1) 직원이 USB QR 리더기로 고객 쿠폰 QR(CPN_*) 스캔
 *   2) GET /tools/coupon/scan → 회원 별명·여권 + 가격 3종 표시
 *   3) [확인] POST /tools/coupon/redeem (UUID 멱등키)
 *   4) [취소(POS 구매취소시)] POST /tools/coupon/cancel
 *
 * 안전장치:
 *   - 더블클릭 디바운스: redeem/cancel 호출 동안 버튼 비활성
 *   - 멱등: tools_request_id 는 화면 진입 시 1회 발급, 동일 스캔에서 재시도 시 hit
 *   - 만료 분기 안내: cancel 응답 coupon_restored 로 분기 표시
 */

import React, { useState, useCallback, useRef } from 'react';
import { Button, ConfigProvider, Modal, theme as antdTheme } from 'antd';
import koKR from 'antd/locale/ko_KR';
import { ArrowLeftOutlined, HistoryOutlined, ReloadOutlined, GiftOutlined, ExclamationCircleOutlined, CheckOutlined, UndoOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useUsbQrReader from '../hooks/useUsbQrReader';
import {
  scanCoupon,
  redeemCoupon,
  cancelCouponRedemption,
  listTodayRedemptions,
} from '../services/api';
import waitingImage from '../assets/coupon-redeem/barcode-waiting.png';
import './AllInOnePage.css';
import './BankWorkspace.css';
import './CouponRedeemPage.css';
import '../components/HistoryPanel.css';

const SCAN_DEBOUNCE_MS = 800; // 같은 코드 연속 스캔 차단

/**
 * 사용취소 결과 문구 — **3분기**.
 *
 * 🔴 예전엔 "복구됨 / 만료" 2분기였다. 2026-08-17 부터 **여권이 이미 취소돼 회수되는**
 *    세 번째 결과가 생겼는데(`recall_reason: 'passport_cancelled'`), 2분기를 그대로 두면
 *    회수된 건이 "쿠폰 만료"로 잘못 안내되어 오퍼레이터가 원인을 찾지 못한다.
 *    권위: docs/2026-08-17-passport-cancel-coupon-revive-issue.md
 *
 * 🔴 멱등 재취소(`idempotent_hit`)도 만료가 아니다. 서버가 두 필드를 채워 보내므로
 *    여기서는 그대로 읽기만 한다 — 판정은 서버가 내린다.
 */
export function buildCancelResultText(result) {
  if (!result) return '';
  if (result.coupon_restored) return '쿠폰 다시 사용 가능';
  if (result.recall_reason === 'passport_cancelled') {
    return '여권이 취소되어 쿠폰이 회수되었습니다';
  }
  if (result.idempotent_hit) return '이미 취소된 건입니다 — 추가 변경 없음';
  return '쿠폰 만료 — 정산 기록만 갱신';
}

function genToolsRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `tools-${crypto.randomUUID()}`;
  }
  return `tools-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function formatWon(n) {
  if (n === null || n === undefined) return '-';
  return `${Number(n).toLocaleString('ko-KR')}원`;
}

const KIND_LABELS = {
  amount: '정액 할인',
  percent: '정률 할인',
  free: '무료 제공',
  n_plus_m: 'N+M (덤)',
  gift: '사은품 증정',
  package_item: '패키지 구성',
};

function kindLabel(kind) {
  return KIND_LABELS[kind] || kind || '-';
}

export default function CouponRedeemPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scanResult, setScanResult] = useState(null); // scan 응답 payload
  const [error, setError] = useState(null); // 화면 표시용 에러 메시지
  const [busy, setBusy] = useState(false); // redeem/cancel 진행 중
  const [confirmedAmount, setConfirmedAmount] = useState(''); // R4 직원 확인 입력
  const [selectedProductId, setSelectedProductId] = useState(null); // 다수 상품 쿠폰 — 직원이 선택한 실제 구매 상품
  const [redeemResult, setRedeemResult] = useState(null); // redeem 응답
  const [cancelResult, setCancelResult] = useState(null); // cancel 응답
  const [storeBlock, setStoreBlock] = useState(null); // 다른 지점 전용 쿠폰 — 전용 모달 메시지
  const [todayList, setTodayList] = useState(null); // 오늘 사용 목록 (null=닫힘)
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const cancelPending = useRef(false);
  const [redeemConfirmOpen, setRedeemConfirmOpen] = useState(false);
  const redeemPending = useRef(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const historyPending = useRef(false);
  const [confirmCancelRow, setConfirmCancelRow] = useState(null); // 목록 사용취소 확인 팝업 대상
  const lastScanRef = useRef({ code: '', at: 0 });
  const scanPendingRef = useRef(false);
  const toolsRequestIdRef = useRef(genToolsRequestId());

  const storeName = user?.store_name || localStorage.getItem('store_name') || '';

  const resetForNewScan = useCallback(() => {
    setScanResult(null);
    setError(null);
    setStoreBlock(null);
    setConfirmedAmount('');
    setSelectedProductId(null);
    setRedeemResult(null);
    setCancelResult(null);
    setTodayList(null);
    setConfirmCancelRow(null);
    toolsRequestIdRef.current = genToolsRequestId();
  }, []);

  // History is an independent overlay: preserve scan, input, result and request ID.
  const openTodayList = useCallback(async (refresh = false) => {
    if (busy || historyPending.current) return;
    if (todayList !== null && refresh !== true) {
      setTodayList(null);
      setConfirmCancelRow(null);
      return;
    }
    historyPending.current = true;
    setHistoryLoading(true);
    setHistoryError(null);
    if (todayList === null) setTodayList([]);
    try {
      const data = (await listTodayRedemptions()) || {};
      setTodayList(data.items || []);
    } catch (err) {
      setHistoryError(err?.response?.data?.message || err?.message || '목록 조회 실패');
    } finally {
      historyPending.current = false;
      setHistoryLoading(false);
    }
  }, [busy, todayList]);

  // 목록 행 사용취소 — 확인 팝업의 [확인]에서 실행. 멱등 ID 는 건마다 새로 생성.
  const cancelFromList = useCallback(async (row) => {
    if (!row || busy) return;
    setBusy(true);
    setHistoryError(null);
    try {
      await cancelCouponRedemption({
        couponIssueId: row.coupon_issue_id,
        toolsRequestId: `${genToolsRequestId()}_listcancel`,
        reason: '현장 사용취소 (오늘 사용 목록)',
      });
      setConfirmCancelRow(null);
      // 목록 갱신 — 취소된 행이 '취소됨' 으로 표시되도록 재조회.
      const data = (await listTodayRedemptions()) || {};
      setTodayList(data.items || []);
    } catch (err) {
      setConfirmCancelRow(null);
      setHistoryError(err?.response?.data?.message || err?.message || '사용취소 실패');
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const handleScan = useCallback(async (rawCode) => {
    if (scanPendingRef.current) return;
    const code = (rawCode || '').trim();
    if (!code) return;

    // 더블 스캔 디바운스
    const now = Date.now();
    if (
      lastScanRef.current.code === code &&
      now - lastScanRef.current.at < SCAN_DEBOUNCE_MS
    ) {
      return;
    }
    lastScanRef.current = { code, at: now };

    scanPendingRef.current = true;
    resetForNewScan();
    setBusy(true);
    try {
      // api.js interceptor 가 response.data 를 unwrap → 반환값이 서버 body 자체.
      const data = (await scanCoupon(code)) || {};
      if (data.success === false) {
        setError(data.message || '스캔 실패');
        return;
      }
      setScanResult(data);
      // M4: 직원 확인(R4) 이지만 정의 할인액이 있으면(amount 등) 입력란에 자동입력.
      //     직원은 실제 상품가에 맞춰 수정 가능 — 확인 정책 유지, 타이핑/오타 부담만 제거.
      if (data.auto_calc === false && data.suggested_discount_amount != null) {
        setConfirmedAmount(String(data.suggested_discount_amount));
      }
    } catch (err) {
      const detail =
        err?.response?.data?.message || err?.message || '서버 오류';
      // 다른 지점 전용 쿠폰 → 일반 배너 대신 고객용 전용 모달로 명확히 안내.
      if (err?.error_code === 'COUPON_STORE_SCOPE') {
        setStoreBlock(detail);
      } else {
        setError(detail);
      }
    } finally {
      scanPendingRef.current = false;
      setBusy(false);
    }
  }, [resetForNewScan]);

  useUsbQrReader({ enabled: !busy && todayList === null && !redeemConfirmOpen && !cancelConfirmOpen, onScan: handleScan, minLength: 8 });

  const handleRedeem = useCallback(async () => {
    if (!scanResult || busy || !redeemConfirmOpen || redeemPending.current) return;
    // 다수 상품 쿠폰 — 정가 있는(auto_calc) 후보를 선택했으면 서버가 그 정가로 자동 계산.
    const choices = scanResult.product_choices || [];
    const selectedChoice = choices.find(
      (c) => String(c.product_id) === String(selectedProductId) && c.auto_calc,
    ) || null;
    const needsConfirm =
      scanResult.auto_calc === false &&
      scanResult.manual_check_required &&
      !selectedChoice;
    if (scanResult.auto_calc === false && choices.length > 0 && !selectedChoice
        && confirmedAmount === '') {
      setError('실제 구매 상품을 선택하세요');
      return;
    }
    let confirmedNum = null;
    if (needsConfirm) {
      const n = Number(confirmedAmount);
      if (!Number.isFinite(n) || n < 0) {
        setError('직원 확인 차감액을 정수(원)로 입력하세요');
        return;
      }
      confirmedNum = Math.floor(n);
    }
    redeemPending.current = true;
    setBusy(true);
    setError(null);
    try {
      const data = await redeemCoupon({
        couponIssueId: scanResult.coupon_issue_id,
        toolsRequestId: toolsRequestIdRef.current,
        confirmedDiscountAmount: confirmedNum,
        selectedProductId: selectedChoice ? selectedChoice.product_id : null,
      });
      setRedeemResult(data || {});
      setRedeemConfirmOpen(false);
    } catch (err) {
      const detail =
        err?.response?.data?.message || err?.message || '사용 처리 실패';
      setError(detail);
    } finally {
      redeemPending.current = false;
      setBusy(false);
    }
  }, [scanResult, busy, confirmedAmount, selectedProductId, redeemConfirmOpen]);

  const handleCancel = useCallback(async () => {
    if (!scanResult || busy || !cancelConfirmOpen || cancelPending.current) return;
    cancelPending.current = true;
    setBusy(true);
    setError(null);
    try {
      const data = await cancelCouponRedemption({
        couponIssueId: scanResult.coupon_issue_id,
        toolsRequestId: `${toolsRequestIdRef.current}_cancel`,
        reason: 'POS 구매 취소',
      });
      setCancelResult(data || {});
      setCancelConfirmOpen(false);
    } catch (err) {
      const detail =
        err?.response?.data?.message || err?.message || '취소 실패';
      setError(detail);
    } finally {
      cancelPending.current = false;
      setBusy(false);
    }
  }, [scanResult, busy, cancelConfirmOpen]);

  const renderPriceBlock = (data) => {
    if (data.kind === 'gift') {
      return (
        <div className="cr-gift-box" data-testid="gift-line">
          <span className="cr-gift-icon" aria-hidden="true"><GiftOutlined /></span>
          <div className="cr-gift-copy"><span className="cr-gift-label">사은품 별도 증정</span><strong>{data.price_triple?.gift_name || '(이름 미정)'}</strong></div>
        </div>
      );
    }
    if (data.auto_calc === false) {
      return (
        <div className="cr-manual-check" data-testid="manual-check">
          <span className="cr-manual-icon">⚠</span>
          <span>{data.manual_check_required || '직원 확인 필요'}</span>
        </div>
      );
    }
    const t = data.price_triple || {};
    return (
      <table className="cr-price-table" data-testid="price-triple">
        <tbody>
          <tr className="cr-price-row">
            <td className="cr-price-label">정가</td>
            <td className="cr-price-value" data-testid="price-original">
              {formatWon(t.original_price)}
            </td>
          </tr>
          <tr className="cr-price-row">
            <td className="cr-price-label">할인 금액</td>
            <td className="cr-price-value" data-testid="price-discount">
              - {formatWon(t.discount_amount)}
            </td>
          </tr>
          <tr className="cr-price-final-row">
            <td className="cr-price-label">
              <strong>최종 결제금액</strong>
            </td>
            <td className="cr-price-value cr-price-final" data-testid="price-final">
              {formatWon(t.final_price)}
            </td>
          </tr>
          {t.applicable_product_name && (
            <tr className="cr-price-product-row">
              <td className="cr-price-product" colSpan={2} data-testid="price-product-name">
                <span className="cr-product-caption">적용 제품</span><span className="cr-product-name">{t.applicable_product_name}</span>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    );
  };

  const showWaiting = !scanResult && !redeemResult && !cancelResult && !error;

  return (
    <ConfigProvider locale={koKR} theme={{ algorithm: antdTheme.defaultAlgorithm, token: {
      colorPrimary: '#007800', colorBgContainer: '#ffffff', colorBgElevated: '#ffffff',
      colorBgLayout: '#f5f5f5', colorText: '#0d0d0d', colorTextSecondary: '#666666',
      colorBorder: '#dcdcdc', colorSuccess: '#189018', borderRadius: 6,
      fontFamily: '"Noto Sans KR", sans-serif',
    } }}>
    <div className="bank-workspace-page cr-page" data-testid="coupon-redeem-page">
      <header className="all-header">
        <div className="all-header-left">
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/')} data-testid="back-btn">메뉴</Button>
          <span className="all-header-title">쿠폰 사용/취소</span>
        </div>
        <div className="all-header-right">
          <Button icon={<HistoryOutlined />} onClick={openTodayList} disabled={busy} data-testid="today-list-btn">금일 사용이력</Button>
          {storeName && <span className="bank-store-badge"><i aria-hidden="true" />{storeName}</span>}
          <span className="all-user-name"><small>ID</small><strong>{user?.name || user?.username || '운영자'}</strong></span>
        </div>
      </header>

      {showWaiting ? (
        <StaffScanWaiting image={waitingImage} alt="쿠폰 바코드를 스캔하는 주렁 프렌즈와 탐험가" busy={busy} busyText="처리 중…" blocked={todayList !== null || redeemConfirmOpen || cancelConfirmOpen || !!storeBlock} onScan={handleScan} guideTestId="scan-hint" guide={<><ScanLabel>주렁주렁 어플</ScanLabel><span>–</span><span className="ssw-phrase"><ScanLabel>쿠폰</ScanLabel><span>의 바코드를 스캔해주세요.</span></span></>} />
      ) : (
      <div className="cr-content">
        <div className="cr-right-panel">
        {error && (
          <div className="cr-error-banner" data-testid="error-banner">
            <span className="cr-error-icon">⚠</span>
            <span>{error}</span>
          </div>
        )}

        {/* 소유자와 쿠폰은 조회·사용 완료·취소 완료에서 같은 위치를 유지한다. */}
        {scanResult && (
          <div className="cr-detail-workspace">
            <div className="cr-owner-summary">
              <p className="cr-card-label">소유자</p>
              <div className="cr-member-row">
                <span
                  className="cr-member-nickname"
                  data-testid="member-nickname"
                >
                  {scanResult.member_nickname || '(별명 없음)'}
                </span>
                {scanResult.has_passport && (
                  <span className="cr-passport-badge" data-testid="passport-badge">
                    여권 보유
                  </span>
                )}
              </div>
            </div>

            <section className="cr-card cr-coupon-ticket">
              <div className="cr-ticket-heading"><p className="cr-card-label">쿠폰 정보</p><span className="cr-coupon-kind">{kindLabel(scanResult.kind)}</span></div>
              <h3 className="cr-coupon-name">
                {scanResult.coupon_name || '(이름 없음)'}
              </h3>
            </section>

            {redeemResult || cancelResult ? (
              <>
                <section className={"cr-card cr-payment-card cr-complete-summary" + (cancelResult ? " cr-cancel-summary" : "")} data-testid={cancelResult ? "cancel-result" : "redeem-result"}>
                  <div className="cr-complete-heading" role="status"><span className="cr-complete-check" aria-hidden="true">{cancelResult ? <UndoOutlined /> : <CheckOutlined />}</span><h2>{cancelResult ? '취소 처리 완료' : '사용 처리 완료'}</h2></div>
                  <div className="cr-complete-detail">
                    <span>{cancelResult ? '쿠폰 상태' : scanResult.kind === 'gift' ? '증정 상품' : '할인 적용'}</span>
                    <strong>{cancelResult ? buildCancelResultText(cancelResult) : scanResult.kind === 'gift' ? (scanResult.price_triple?.gift_name || '(이름 미정)') : formatWon(redeemResult.discount_amount)}</strong>
                  </div>
                  {!cancelResult && redeemResult?.idempotent_hit && <p className="cr-complete-note">이미 처리된 내역을 확인했습니다.</p>}
                </section>
                <div className="cr-actions cr-complete-actions">
                  <button type="button" className="cr-btn cr-btn-primary" onClick={resetForNewScan} disabled={busy} data-testid="next-coupon-btn">다음 쿠폰</button>
                  <p className="cr-next-scan-hint">다른 쿠폰을 보려면 새 바코드를 스캔해 주세요.</p>
                  {!cancelResult && <button type="button" className="cr-complete-cancel" onClick={() => { setError(null); setCancelConfirmOpen(true); }} disabled={busy} data-testid="cancel-btn">사용 취소</button>}
                </div>
              </>
            ) : (
              <>
            <div className="cr-card cr-payment-card">
              <p className="cr-card-label">할인 및 결제 정보</p>
              {(() => {
                const choices = scanResult.product_choices || [];
                const sel = choices.find(
                  (c) => String(c.product_id) === String(selectedProductId),
                ) || null;
                if (scanResult.auto_calc === false && choices.length > 0) {
                  return (
                    <>
                      <div className="cr-product-choices" data-testid="product-choices">
                        <p className="cr-choices-label">실제 구매 상품을 선택하세요</p>
                        {choices.map((c) => (
                          <button
                            key={c.product_id}
                            type="button"
                            className={`cr-choice-btn${
                              String(selectedProductId) === String(c.product_id)
                                ? ' cr-choice-btn--selected' : ''
                            }`}
                            onClick={() => setSelectedProductId(c.product_id)}
                            data-testid={`choice-${c.product_id}`}
                          >
                            {c.product_name}
                            {c.default_price != null && c.default_price > 0
                              ? ` (${formatWon(c.default_price)})` : ' (정가 미등록)'}
                          </button>
                        ))}
                      </div>
                      {sel && sel.auto_calc && renderPriceBlock({
                        kind: scanResult.kind, auto_calc: true, price_triple: sel.price_triple,
                      })}
                      {sel && !sel.auto_calc && renderPriceBlock(scanResult)}
                      {!sel && (
                        <div className="cr-manual-check" data-testid="manual-check">
                          <span className="cr-manual-icon">⚠</span>
                          <span>상품 선택 시 자동 계산 — 또는 아래에 차감액 직접 입력</span>
                        </div>
                      )}
                    </>
                  );
                }
                return renderPriceBlock(scanResult);
              })()}

              {scanResult.auto_calc === false && scanResult.kind !== 'gift'
                && !(scanResult.product_choices || []).some(
                  (c) => String(c.product_id) === String(selectedProductId) && c.auto_calc,
                ) && (
                <div className="cr-confirm-input">
                  <label htmlFor="confirmed-amount" className="cr-confirm-label">
                    직원 확인 차감액 (원)
                  </label>
                  <input
                    id="confirmed-amount"
                    type="number"
                    inputMode="numeric"
                    min="0"
                    step="100"
                    value={confirmedAmount}
                    onChange={(e) => setConfirmedAmount(e.target.value)}
                    data-testid="confirmed-amount-input"
                  />
                  {scanResult.suggested_discount_amount != null && (
                    <p className="cr-confirm-hint" data-testid="confirmed-amount-hint">
                      쿠폰 정의 할인액 {formatWon(scanResult.suggested_discount_amount)} 자동입력 —
                      실제 상품가에 맞게 수정할 수 있습니다.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* 주 동작: 사용 진행 단독. POS 구매 취소는 차감 기록이 생긴 뒤에만 의미가
                있으므로(미사용 쿠폰에 누르면 NO_ACTIVE_REDEMPTION 에러) 스캔 단계에서
                제거하고 "사용 처리 완료" 결과 카드로 이동 (①안). */}
            <div className="cr-actions">
              <button
                className="cr-btn cr-btn-primary"
                onClick={() => { setError(null); setRedeemConfirmOpen(true); }}
                disabled={busy}
                data-testid="redeem-btn"
              >
                {busy ? '처리중…' : '사용 진행'}
              </button>
              <p className="cr-next-scan-hint">다른 쿠폰을 보려면 새 바코드를 스캔해 주세요.</p>
            </div>
              </>
            )}
          </div>
        )}

        </div>
      </div>

      )}

        {/* 오늘 사용 목록 — 본문 상태를 보존하며, 열린 동안 새 스캔 입력은 일시 중지 */}
        {todayList !== null && (
          <div className="cr-history-backdrop" onClick={() => { if (!busy && !historyLoading && !confirmCancelRow) setTodayList(null); }}>
          <aside className="cr-history-panel" role="dialog" aria-modal="true" aria-labelledby="cr-history-title" onClick={(e) => e.stopPropagation()}>
            <div className="cr-history-head tools-history-head"><h2 id="cr-history-title">금일 사용이력</h2>
              <div className="tools-history-header-actions">
                <Button className="tools-history-refresh" icon={<ReloadOutlined />} disabled={busy || historyLoading || Boolean(confirmCancelRow)} onClick={() => openTodayList(true)}>새로고침</Button>
                <Button className="tools-history-close" disabled={busy || historyLoading || Boolean(confirmCancelRow)} onClick={() => setTodayList(null)}>닫기</Button>
              </div>
            </div>
            <div className="cr-history-tools tools-history-tools">
              <div className="tools-history-summary-cards" aria-label="쿠폰 처리 현황">
                <div className="tools-history-summary-card" data-testid="history-used-count"><span>사용 완료</span><strong>{historyLoading || historyError ? '—' : todayList.filter(row => row.state === 'active').length}<small>건</small></strong></div>
                <div className="tools-history-summary-card is-cancel" data-testid="history-cancelled-count"><span>사용 취소</span><strong>{historyLoading || historyError ? '—' : todayList.filter(row => row.state === 'refunded').length}<small>건</small></strong></div>
              </div>
            </div>
            {historyError && <div className="cr-error-banner" role="alert" data-testid="history-error">{historyError}</div>}
            {historyLoading && <p role="status">내역을 불러오는 중…</p>}
            <div className="cr-today-list" data-testid="today-list">
            {todayList.length === 0 && !historyLoading && !historyError && (
              <p className="cr-today-empty">오늘 이 지점에서 사용 처리된 쿠폰이 없습니다.</p>
            )}
            {todayList.map((row) => (
              <div className="cr-today-row" key={row.redemption_id}
                data-testid={`today-row-${row.redemption_id}`}>
                <div className="cr-today-info">
                  <div className="cr-today-status-time">
                    <span className={`cr-today-status ${row.state === 'refunded' ? 'is-cancel' : ''}`}>{row.state === 'refunded' ? '취소됨' : '사용 완료'}</span>
                    <span className="cr-today-date">{row.redeemed_at}</span>
                  </div>
                  <span className="cr-today-phone">
                    <strong>{String(row.phone_masked || '').match(/\d{4}$/)?.[0] || '번호 미확인'}</strong><span>{row.nickname ? `(${row.nickname})` : '(별명 없음)'}</span>
                  </span>
                  <span className="cr-today-coupon-name">{row.coupon_name || '(이름 없음)'}</span>
                  <span className="cr-today-detail"><strong>{formatWon(row.discount_amount)}</strong><small>차감</small></span>
                  <div className="cr-today-actions">
                    {row.state !== 'refunded' && (
                      <button
                        className="cr-btn cr-btn-secondary cr-today-cancel"
                        onClick={() => setConfirmCancelRow(row)}
                        disabled={busy}
                        data-testid={`today-cancel-${row.redemption_id}`}
                      >사용취소</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          </aside>
          </div>
        )}


      <Modal open={redeemConfirmOpen} centered width={760} className="zb-withdraw-confirm cr-redeem-confirm"
        title={<span className="cr-confirm-title"><ExclamationCircleOutlined />쿠폰을 사용 처리할까요?</span>}
        closable={false} maskClosable={false} keyboard={!busy}
        onCancel={() => { if (!redeemPending.current) setRedeemConfirmOpen(false); }}
        footer={<div className="ant-modal-confirm-btns">
          <Button disabled={busy} onClick={() => setRedeemConfirmOpen(false)} data-testid="redeem-confirm-back">돌아가기</Button>
          <Button type="primary" loading={busy} disabled={busy} onClick={handleRedeem} data-testid="redeem-confirm-ok">사용 처리</Button>
        </div>}>
        <dl className="zb-confirm-details">
          <div><dt>소유자</dt><dd>{scanResult?.member_nickname || '(별명 없음)'}</dd></div>
          <div><dt>쿠폰</dt><dd>{scanResult?.coupon_name || '(이름 없음)'}</dd></div>
          {scanResult?.kind === 'gift'
            ? <div><dt>증정 사은품</dt><dd>{scanResult?.price_triple?.gift_name || '(이름 미정)'}</dd></div>
            : <div><dt>할인 금액</dt><dd>{(() => {
              const selected = (scanResult?.product_choices || []).find(c => String(c.product_id) === String(selectedProductId) && c.auto_calc);
              if (selected) return formatWon(selected.price_triple?.discount_amount);
              if (scanResult?.auto_calc === false) return confirmedAmount === '' ? '입력 확인 필요' : formatWon(confirmedAmount);
              return formatWon(scanResult?.price_triple?.discount_amount);
            })()}</dd></div>}
        </dl>
        <p className="zb-confirm-note">소유자와 쿠폰 내용을 확인한 뒤 사용 처리해 주세요.</p>
        {error && <div className="cr-error-banner" role="alert" data-testid="redeem-confirm-error">{error}</div>}
      </Modal>

      <Modal open={cancelConfirmOpen} centered width={760} className="zb-withdraw-confirm cr-redeem-confirm"
        title={<span className="cr-confirm-title"><ExclamationCircleOutlined />방금 사용한 쿠폰을 취소할까요?</span>}
        closable={false} maskClosable={false} keyboard={!busy}
        onCancel={() => { if (!cancelPending.current) setCancelConfirmOpen(false); }}
        footer={<div className="ant-modal-confirm-btns">
          <Button disabled={busy} onClick={() => setCancelConfirmOpen(false)} data-testid="recent-cancel-back">돌아가기</Button>
          <Button className="cr-confirm-cancel-action" loading={busy} disabled={busy} onClick={handleCancel} data-testid="recent-cancel-ok">사용 취소</Button>
        </div>}>
        <dl className="zb-confirm-details"><div><dt>소유자</dt><dd>{scanResult?.member_nickname || '(별명 없음)'}</dd></div><div><dt>쿠폰</dt><dd>{scanResult?.coupon_name || '(이름 없음)'}</dd></div></dl>
        <p className="zb-confirm-note">POS 구매도 취소한 경우 진행해 주세요. 쿠폰 복원 여부는 처리 결과에서 확인할 수 있습니다.</p>
        {error && <div className="cr-error-banner" role="alert" data-testid="recent-cancel-error">{error}</div>}
      </Modal>

      {/* 오늘 목록 사용취소 — 확인 팝업 (감사 고지: 취소 이력은 운영자툴에서 확인 가능) */}
      {confirmCancelRow && (
        <div
          className="cr-block-overlay"
          onClick={() => setConfirmCancelRow(null)}
          data-testid="cancel-confirm-modal"
        >
          <div className="cr-block-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cr-block-icon">↩</div>
            <div className="cr-block-title">쿠폰 사용을 취소할까요?</div>
            <div className="cr-block-msg">
              {confirmCancelRow.phone_masked} · {confirmCancelRow.coupon_name} ·{' '}
              {formatWon(confirmCancelRow.discount_amount)}
              <br />
              취소하면 쿠폰은 고객 쿠폰함에 복원됩니다.
              <br />
              <strong>쿠폰 사용 취소 이력은 운영자툴에서 확인할 수 있습니다.</strong>
            </div>
            <div className="cr-confirm-actions">
              <button
                className="cr-block-btn cr-block-btn-secondary"
                onClick={() => setConfirmCancelRow(null)}
                disabled={busy}
              >
                닫기
              </button>
              <button
                className="cr-block-btn"
                onClick={() => cancelFromList(confirmCancelRow)}
                disabled={busy}
                data-testid="cancel-confirm-ok"
              >
                {busy ? '처리중…' : '확인 (사용취소)'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 다른 지점 전용 쿠폰 — 고객 안내 전용 모달(B). 일반 에러 배너보다 명확히. */}
      {storeBlock && (
        <div
          className="cr-block-overlay"
          onClick={() => setStoreBlock(null)}
          data-testid="store-block-modal"
        >
          <div className="cr-block-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cr-block-icon">🏬</div>
            <div className="cr-block-title">다른 지점 전용 쿠폰입니다</div>
            <div className="cr-block-msg">{storeBlock}</div>
            <button className="cr-block-btn" onClick={() => setStoreBlock(null)}>
              확인
            </button>
          </div>
        </div>
      )}
    </div>
    </ConfigProvider>
  );
}
